import { ReactNode } from 'react';
import { Box, ButtonBase, Typography, alpha } from '@mui/material';
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';

export interface ChoiceOption<T extends string> {
  value: T;
  label: string;
  description?: string;
  icon?: ReactNode;
}

interface ChoiceBoxesProps<T extends string> {
  label: string;
  value: T;
  options: ChoiceOption<T>[];
  onChange: (value: T) => void;
  /** Columns from `sm` up (one column on phones). */
  columns?: number;
  disabled?: boolean;
}

/**
 * A few options as equal boxes instead of a select: the chosen one has a
 * primary border, a light primary background and a check.
 */
function ChoiceBoxes<T extends string>({
  label,
  value,
  options,
  onChange,
  columns = options.length,
  disabled = false
}: ChoiceBoxesProps<T>) {
  return (
    <Box>
      <Typography variant="subtitle2" color="text.secondary" mb={1}>
        {label}
      </Typography>
      <Box
        role="radiogroup"
        aria-label={label}
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: `repeat(${columns}, 1fr)` },
          gap: 1
        }}
      >
        {options.map((option) => {
          const selected = option.value === value;
          return (
            <ButtonBase
              key={option.value}
              role="radio"
              aria-checked={selected}
              disabled={disabled}
              onClick={() => onChange(option.value)}
              sx={(theme) => ({
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                gap: 1,
                width: '100%',
                px: 1.5,
                py: 1,
                textAlign: 'left',
                borderRadius: 1,
                border: '1px solid',
                borderColor: selected
                  ? theme.palette.primary.main
                  : theme.palette.divider,
                boxShadow: selected
                  ? `0 0 0 1px ${theme.palette.primary.main}`
                  : 'none',
                backgroundColor: selected
                  ? alpha(theme.palette.primary.main, 0.08)
                  : theme.palette.background.paper,
                '&:hover': { borderColor: theme.palette.primary.main },
                '&.Mui-focusVisible': {
                  boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.4)}`
                },
                '&.Mui-disabled': { opacity: 0.6 }
              })}
            >
              {option.icon}
              <Box flex={1} minWidth={0}>
                <Typography variant="body2" fontWeight={600}>
                  {option.label}
                </Typography>
                {option.description && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ textTransform: 'none', display: 'block' }}
                  >
                    {option.description}
                  </Typography>
                )}
              </Box>
              {selected && (
                <CheckCircleRounded color="primary" fontSize="small" />
              )}
            </ButtonBase>
          );
        })}
      </Box>
    </Box>
  );
}

export default ChoiceBoxes;
